import { UserRole, type AuthUser, type ProfileDetail } from '@teentack/shared';
import { Badge } from '@/components/ui/Badge';
import { BadgeCheckIcon, MapPinIcon, ShieldCheckIcon } from '@/components/ui/Icons';
import { ROLE_LABELS, VERIFICATION_BADGES, initialsOf, locationLabel } from './profile-display';

/**
 * The identity band at the top of the profile: who this is, what they are on
 * the platform, and the trust signals that decide what they can do next.
 */
export function ProfileHero({
  user,
  profile,
  action,
}: {
  user: AuthUser;
  profile: ProfileDetail;
  action?: React.ReactNode;
}) {
  const verification = VERIFICATION_BADGES[profile.verificationStatus];
  const place = locationLabel(profile);

  const subtitle =
    profile.role === UserRole.JOB_POSTER
      ? profile.professionalTitle
      : profile.role === UserRole.TEEN && profile.isMinor !== null
        ? profile.isMinor
          ? 'Teen — guardian approval required'
          : 'Teen — no guardian approval needed'
        : null;

  return (
    <section className="overflow-hidden rounded-3xl border border-line bg-white shadow-lift">
      <div className="bg-brand-gradient h-24 sm:h-28" />

      <div className="px-6 pb-6 sm:px-8 sm:pb-8">
        <div className="-mt-12 flex flex-col gap-5 sm:-mt-14 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex items-end gap-4">
            {profile.photoUrl ? (
              <img
                src={profile.photoUrl}
                alt=""
                className="size-24 rounded-2xl border-4 border-white bg-white object-cover shadow-lift sm:size-28"
              />
            ) : (
              <span
                aria-hidden="true"
                className="grid size-24 place-items-center rounded-2xl border-4 border-white bg-slate-100 text-2xl font-extrabold text-slate-500 shadow-lift sm:size-28 sm:text-3xl"
              >
                {initialsOf(profile.firstName, profile.lastName)}
              </span>
            )}

            <div className="pb-1">
              <h1 className="text-2xl font-extrabold text-ink sm:text-3xl">
                {profile.firstName} {profile.lastName}
              </h1>
              {subtitle && <p className="mt-1 text-sm font-medium text-slate-500">{subtitle}</p>}
              {place && (
                <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
                  <MapPinIcon className="size-4 text-slate-400" />
                  {place}
                </p>
              )}
            </div>
          </div>

          {action && <div className="shrink-0 sm:pb-1">{action}</div>}
        </div>

        <div className="mt-6 flex flex-wrap gap-2 border-t border-line pt-5">
          <Badge tone="brand">{ROLE_LABELS[profile.role] ?? profile.role}</Badge>

          {verification && (
            <Badge tone={verification.tone} icon={<BadgeCheckIcon className="size-3.5" />}>
              {verification.label}
            </Badge>
          )}

          <Badge tone={user.emailVerified ? 'safe' : 'neutral'}>
            {user.emailVerified ? 'Email verified' : 'Email unverified'}
          </Badge>

          <Badge tone={user.phoneVerified ? 'safe' : 'neutral'}>
            {user.phoneVerified ? 'Mobile verified' : 'Mobile unverified'}
          </Badge>

          {profile.role === UserRole.TEEN && profile.isMinor && (
            <Badge
              tone={profile.guardianApproved ? 'safe' : 'sun'}
              icon={<ShieldCheckIcon className="size-3.5" />}
            >
              {profile.guardianApproved ? 'Guardian approved' : 'Awaiting guardian'}
            </Badge>
          )}
        </div>
      </div>
    </section>
  );
}
